/**
 * ルート定義の単一ソース。
 *
 * design/js/app.jsx の CRUMB_MAP / AUTH_ROUTES、design/js/screens-settings.jsx の
 * SECTIONS と完全一致させる (utils/__tests__/route.spec.ts に snapshot assertion あり)。
 *
 * 設計判断:
 *   - HashRouter のみ (HTML5 history は使わない)
 *   - AUTH_ROUTES は ReadonlySet。完全一致のみ — 部分一致 bypass を防ぐ
 *   - KNOWN_HEADS は private (utils/route.ts の resolveRoute 内部で使用)
 *   - パンくず / 設定サブの値は **i18n key** を保持 (`common.nav.*` / `common.settings_sub.*`)。
 *     描画側で `t(key)` を介して言語別表示にする。
 */

/** 認証画面 (split-screen / 未認証で参照可) */
export const AUTH_ROUTES: ReadonlySet<string> = new Set<string>([
  'login',
  'register',
  'forgot',
  'forgot/recovery-key',
  'forgot/emergency',
  'forgot/hint',
  'forgot/reset',
  'lock',
  'terms',
  'privacy',
])

/**
 * ログイン後シェルが受け付けるトップレベル head。
 *
 * v3.2 plan に基づく。`org` は design/js/app.jsx:120 の legacy alias だが
 * v3.2 仕様には存在しないため除外する。
 */
export const KNOWN_HEADS: ReadonlySet<string> = new Set<string>([
  'home',
  'vault',
  'send',
  'reports',
  'tools',
  'settings',
  // Track F: `#emergency/vault/:id` (緊急アクセスで granter の保管庫を閲覧)。
  'emergency',
])

/**
 * パンくず — i18n key を保持する。描画側で `t(key)` を当てる。
 * design/js/app.jsx の CRUMB_MAP の文言と 1:1 で対応 (`common.nav.*`)。
 */
export const CRUMB_KEYS: Readonly<Record<string, readonly string[]>> = {
  home: ['common.nav.home'],
  vault: ['common.nav.vault'],
  'vault/new': ['common.nav.vault', 'common.nav.vault_new'],
  'vault/trash': ['common.nav.vault', 'common.nav.vault_trash'],
  send: ['common.nav.send'],
  'send/new': ['common.nav.send', 'common.nav.send_new'],
  reports: ['common.nav.reports'],
  tools: ['common.nav.tools'],
  'tools/generator': ['common.nav.tools', 'common.nav.tools_generator'],
  'tools/import': ['common.nav.tools', 'common.nav.tools_import'],
  'tools/export': ['common.nav.tools', 'common.nav.tools_export'],
  settings: ['common.nav.settings'],
  terms: ['common.nav.terms'],
  privacy: ['common.nav.privacy'],
}

/**
 * レポート詳細のサブ区分 — i18n key を保持する。
 * design/js/screens-reports.jsx REPORT_DEFS と完全一致。
 */
export const REPORTS_SUB_KEYS: Readonly<Record<string, string>> = {
  reused: 'common.reports_sub.reused',
  weak: 'common.reports_sub.weak',
  unsecured: 'common.reports_sub.unsecured',
  'inactive-2fa': 'common.reports_sub.inactive_2fa',
}

/**
 * 設定画面のサブ区分 — i18n key を保持する。
 * v3.2 plan §4.7 / design/js/screens-settings.jsx:7-13 SECTIONS と完全一致。
 */
export const SETTINGS_SUB_KEYS: Readonly<Record<string, string>> = {
  account: 'common.settings_sub.account',
  security: 'common.settings_sub.security',
  '2fa': 'common.settings_sub.2fa',
  emergency: 'common.settings_sub.emergency',
  preferences: 'common.settings_sub.preferences',
  domain: 'common.settings_sub.domain',
  'delete-account': 'common.settings_sub.delete_account',
}

/** ログイン画面 route。redirect 先などで再利用するための定数。 */
export const LOGIN_ROUTE = 'login'

/** ロック画面 route。idleLock 満了 / 明示的ロック後の遷移先。 */
export const LOCK_ROUTE = 'lock'

/** マスターパスワード / PIN いずれかで unlock 成功後の既定遷移先 (= home dashboard)。
 * useUnlockForm と LockPage.onPinComplete で共通参照する (review I-8 で divergent
 * destination `#vault` vs `#home` を解消)。 */
export const POST_UNLOCK_ROUTE = 'home'

/**
 * マーケティング 3 ページ (T-M、`design/html/*.html` 由来)。
 * 未認証 / ロック中問わず誰でも閲覧可能 (AUTH_ROUTES と同等の public 扱い)。
 * Sidenav / Topbar を持たない MarketingLayout で render する。
 */
export const LANDING_ROUTE = 'landing'
export const DOCS_ROUTE = 'docs'
export const CONTACT_ROUTE = 'contact'
export const DOWNLOAD_ROUTE = 'download'
export const SUPPORT_ROUTE = 'support'

export const MARKETING_ROUTES: ReadonlySet<string> = new Set<string>([
  LANDING_ROUTE,
  DOCS_ROUTE,
  CONTACT_ROUTE,
  DOWNLOAD_ROUTE,
  SUPPORT_ROUTE,
])
